import { useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { dealerApi, CreditRequest } from '../../services/dealerApi';
import Money from './Money';
import { dealerErrorMessage, fmt } from '../../utils/dealer';

interface ProformaAcceptDialogProps {
  token: string;
  request: CreditRequest;
  onClose: () => void;
  onAccepted: (r: CreditRequest) => void;
}

/**
 * Spec 085 (Flow B step 4 / Flow C step 3, BR-6) — accept a proforma: the dealer commits to pay the total
 * due by bank transfer with the proforma number as reference. Per-buyer requests and pack orders accept
 * on their own endpoints.
 */
export default function ProformaAcceptDialog({ token, request, onClose, onAccepted }: ProformaAcceptDialogProps) {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = request.kind === 'PACK'
        ? await dealerApi.acceptPackOrder(request.id, token)
        : await dealerApi.acceptRequest(request.id, token);
      onAccepted(r);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="proforma-accept-title">
      <div className="modal-content dealer-dialog">
        <h3 id="proforma-accept-title">{fmt(t('dealer.proforma.acceptTitle'), { number: request.number })}</h3>
        <dl className="dealer-dl">
          <dt>{t('dealer.quote.faceValue')}</dt><dd><Money value={request.faceValueAed} /></dd>
          <dt>{t('dealer.quote.discount')}</dt><dd dir="ltr">{request.discountPct}%</dd>
          <dt>{t('dealer.quote.payable')}</dt><dd><Money value={request.payableAed} /></dd>
          <dt>{t('dealer.quote.vat')}</dt><dd><Money value={request.vatAed} /></dd>
          <dt className="dealer-dl__total">{t('dealer.quote.totalDue')}</dt>
          <dd className="dealer-dl__total"><Money value={request.totalDueAed} /></dd>
        </dl>
        <p>{fmt(t('dealer.proforma.commitText'), { number: request.number })}</p>
        {error && <div className="error-message">{error}</div>}
        <div className="dealer-actions dealer-actions--end">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={accept} disabled={saving}>
            {saving ? t('dealer.common.saving') : t('dealer.proforma.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
